import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, type ApiClient } from '../api/client';
import type { DraftReview, Problem } from '../api/types';
import { toProblem } from './useAsync';

export interface DraftReviewState {
  /** 今の appId のレビュー。まだ読んでいない・Draft が無いときは null。 */
  review: DraftReview | null;
  loading: boolean;
  /** 404 以外の取得の失敗。取り直しの失敗では review を残したまま出す。 */
  problem: Problem | null;
  /** 404（draft_not_found / not_found）。problem は null のまま。 */
  notFound: boolean;
  reload(): void;
  /** validateDraft の応答の review で置き換える。失敗は validateProblem に入れて投げない。 */
  validate(): Promise<void>;
  validating: boolean;
  validateProblem: Problem | null;
}

export interface DraftReviewOptions {
  api: ApiClient;
  workspaceId: string;
  appId: string | null;
  /** false か appId が null なら API を呼ばない（fixture モード、Draft が無いとき）。 */
  enabled: boolean;
  /** 変わると取り直す。Studio は Draft の headRevisionId を渡す（保存・反映・段階の変更で head が進む）。 */
  reloadKey?: unknown;
}

/**
 * GET A/draft/review の snapshot と POST A/draft:validate（計画の S4 D10・D11）。
 * SSE（studio.report / studio.revision）を受けたら呼び出し側が reload() する。
 */
export function useDraftReview({ api, workspaceId, appId, enabled, reloadKey }: DraftReviewOptions): DraftReviewState {
  const [review, setReview] = useState<DraftReview | null>(null);
  const [loading, setLoading] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [validating, setValidating] = useState(false);
  // 失敗した検証の対象も一緒に持ち、別の app へ移った後には出さない。
  const [validateProblem, setValidateProblem] = useState<{ key: string; problem: Problem } | null>(null);
  const [token, setToken] = useState(0);
  // 検証の応答が、選び直した後の別の app に混ざらないよう、いまの対象を持つ。
  const target = enabled && appId !== null ? `${workspaceId}/${appId}` : null;
  const current = useRef<string | null>(null);
  current.current = target;

  useEffect(() => {
    if (!enabled || appId === null) {
      setReview(null);
      setProblem(null);
      setNotFound(false);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    api
      .getDraftReview(workspaceId, appId)
      .then((loaded) => {
        if (!active) return;
        setReview(loaded);
        setProblem(null);
        setNotFound(false);
        setLoading(false);
      })
      .catch((error: unknown) => {
        if (!active) return;
        if (error instanceof ApiError && error.status === 404) {
          setReview(null);
          setProblem(null);
          setNotFound(true);
        } else {
          setProblem(toProblem(error));
        }
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [api, workspaceId, appId, enabled, reloadKey, token]);

  const reload = useCallback(() => setToken((value) => value + 1), []);

  const validate = useCallback(async () => {
    if (!enabled || appId === null) return;
    const key = `${workspaceId}/${appId}`;
    setValidating(true);
    setValidateProblem(null);
    try {
      const result = await api.validateDraft(workspaceId, appId);
      if (current.current !== key) return;
      setReview(result.review);
      setNotFound(false);
      setProblem(null);
    } catch (error) {
      if (current.current === key) setValidateProblem({ key, problem: toProblem(error) });
    } finally {
      setValidating(false);
    }
  }, [api, workspaceId, appId, enabled]);

  return {
    // 別の app へ移った直後に、前の app のレビューを出さない。
    review: review !== null && review.appId === appId ? review : null,
    loading,
    problem,
    notFound,
    reload,
    validate,
    validating,
    // 別の app へ移った直後に、前の app の検証の失敗を出さない。
    validateProblem: validateProblem !== null && validateProblem.key === target ? validateProblem.problem : null,
  };
}
